import { classNames } from 'primereact/utils';
import { TriangleIcon } from '@repo/atomic-components';
import { useAiProcessedDocuments, useAiProcessedDownloads } from './hooks';
import { AiProcessedCard } from './components';
import { AccessControlAction, AppRoutes } from '@/constants';
import { BundleDownloadStatusType } from '@/api';
import { useAccessControl } from '@/hooks';

export const AiProcessedDocuments = () => {
  const { caseId, caseOverviewCards } = useAiProcessedDocuments();
  const {
    fullBundleDownloadStatus,
    lightBundleDownloadStatus,
    fullBundleDownloadProgress,
    lightBundleDownloadProgress,
    isGeneratingFull,
    isGeneratingLight,
    fullBundleRequestError,
    lightBundleRequestError,
    fullIndexDownloadError,
    lightIndexDownloadError,
    handleDownloadFullBundle,
    handleDownloadLightBundle,
    handleDownloadFullBundleIndex,
    handleDownloadLightBundleIndex,
    handleDownloadTimeline,
    handleDownloadChronology,
    handleFullBundleRequestDownloadManually,
    handleLightBundleRequestDownloadManually,
    chronologyTemplates,
    indexTemplates,
  } = useAiProcessedDownloads();

  const isExportBundlesTimelinesChronologies = useAccessControl(
    AccessControlAction.EXPORT_BUNDLES_TIMELINES_CHRONOLOGIES,
  );
  const isViewBundles = useAccessControl(AccessControlAction.VIEW_BUNDLES);

  if (
    !caseOverviewCards.fullBundleCard &&
    !caseOverviewCards.lightBundleCard &&
    !caseOverviewCards.timelineBundleCard &&
    !caseOverviewCards.chronologyBundleCard
  ) {
    // Empty state — mirrors the raw-medical-records empty state on
    // the sibling tab: 32px play-shaped icon + bold headline + muted
    // sub-line. Same colour/size tokens as the FileDropOverlay so
    // both empty states feel like the same component family.
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <TriangleIcon className="text-fletchers-black-900 mb-6" style={{ width: '32px', height: '32px' }} />
        <p className="text-fletchers-black-900 mb-2 text-xl font-semibold">Start AI processing to view outputs</p>
        <p className="text-fletchers-gray-200 text-sm">Once processing completes, your outputs will appear here.</p>
      </div>
    );
  }

  return (
    // 2×2 grid that fills the viewport — cards shrink to the column
    // (`minmax(0,1fr)`) so two side-by-side never exceed the row,
    // and each row is `minmax(0,1fr)` so two stacked cells never
    // overflow vertically. `overflow-hidden` plus the `min-h-0` /
    // `min-w-0` clamps prevent any single overgrown child from
    // popping a scrollbar. 16px gap between cells, no outer padding
    // (the page-level wrapper in CaseItemLayout already handles
    // outer page padding).
    <div
      className={classNames(
        'grid h-full w-full grid-cols-[repeat(2,minmax(0,1fr))] grid-rows-[repeat(2,minmax(0,1fr))]',
        'gap-4 overflow-hidden',
        '[&>*]:min-h-0 [&>*]:min-w-0',
      )}
    >
      {caseOverviewCards.fullBundleCard && isViewBundles && (
        <AiProcessedCard
          name="Full Bundle"
          pages={caseOverviewCards.fullBundleCard.numberOfPages}
          entries={null}
          updatedDate={caseOverviewCards.fullBundleCard.lastUpdated}
          createdDate={caseOverviewCards.fullBundleCard.createdAt}
          routeTo={`${AppRoutes.FullBundle}/${caseOverviewCards.fullBundleCard.bundleId}`}
          handleDownload={
            isExportBundlesTimelinesChronologies && fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY
              ? handleDownloadFullBundle
              : undefined
          }
          handleDownloadIndex={isExportBundlesTimelinesChronologies ? handleDownloadFullBundleIndex : undefined}
          indexTemplates={indexTemplates}
          handleBundleRequestDownloadManually={
            isExportBundlesTimelinesChronologies ? handleFullBundleRequestDownloadManually : undefined
          }
          downloadProgress={fullBundleDownloadProgress}
          isDownloadLoading={isGeneratingFull}
          isDownloadError={fullBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED}
          isGenerating={isGeneratingFull}
          bundleRequestError={fullBundleRequestError}
          indexDownloadError={fullIndexDownloadError}
        />
      )}

      {caseOverviewCards.lightBundleCard && isViewBundles && (
        <AiProcessedCard
          name="Light Bundle"
          pages={caseOverviewCards.lightBundleCard.numberOfPages}
          entries={null}
          updatedDate={caseOverviewCards.lightBundleCard.lastUpdated}
          createdDate={caseOverviewCards.lightBundleCard.createdAt}
          routeTo={`${AppRoutes.LightBundle}/${caseOverviewCards.lightBundleCard.bundleId}`}
          handleDownload={
            isExportBundlesTimelinesChronologies &&
            lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_READY
              ? handleDownloadLightBundle
              : undefined
          }
          handleDownloadIndex={isExportBundlesTimelinesChronologies ? handleDownloadLightBundleIndex : undefined}
          indexTemplates={indexTemplates}
          handleBundleRequestDownloadManually={
            isExportBundlesTimelinesChronologies ? handleLightBundleRequestDownloadManually : undefined
          }
          downloadProgress={lightBundleDownloadProgress}
          isDownloadLoading={isGeneratingLight}
          isDownloadError={lightBundleDownloadStatus === BundleDownloadStatusType.DOWNLOAD_PREPARATION_FAILED}
          isGenerating={isGeneratingLight}
          bundleRequestError={lightBundleRequestError}
          indexDownloadError={lightIndexDownloadError}
        />
      )}

      {caseOverviewCards.timelineBundleCard && (
        <AiProcessedCard
          name="Timeline"
          pages={null}
          entries={caseOverviewCards.timelineBundleCard.numberOfEntries}
          updatedDate={caseOverviewCards.timelineBundleCard.lastUpdated}
          createdDate={caseOverviewCards.timelineBundleCard.createdAt}
          routeTo={`${AppRoutes.Timeline}/${caseId}`}
          handleDownload={isExportBundlesTimelinesChronologies ? handleDownloadTimeline : undefined}
          downloadButtonLabel="Download DOCX"
        />
      )}
      {caseOverviewCards.chronologyBundleCard && (
        <AiProcessedCard
          name="Chronology"
          pages={null}
          entries={caseOverviewCards.chronologyBundleCard.numberOfEntries}
          updatedDate={caseOverviewCards.chronologyBundleCard.lastUpdated}
          createdDate={caseOverviewCards.chronologyBundleCard.createdAt}
          routeTo={`${AppRoutes.Chronology}/${caseId}`}
          handleDownloadWithPageNumberChoice={
            isExportBundlesTimelinesChronologies ? handleDownloadChronology : undefined
          }
          chronologyTemplates={chronologyTemplates}
          downloadButtonLabel="Download DOCX"
        />
      )}
    </div>
  );
};
